import React, { useMemo } from "react";
import ServicePage from "./pages/ServicePage";
import SectionCoverPage from "./pages/SectionCoverPage";
import ServiceSchedulingPage from "./pages/ServiceSchedulingPage";
import {
  CoverPage,
  NewReportData,
  SchedulingPage,
  ServicesPage,
} from "../types/newReportTypes";
import { calculateSchedulingPageCount } from "../hooks/usePagination";

type Props = {
  data: NewReportData;
};

/**
 * Renders all pages of a new report (cover, scheduling, services)
 * This component is shared between NewReport and ScaledNewReportPages
 */
const NewReportPagesContent = ({ data }: Props) => {
  // Calculate how many pages the scheduling section will take
  const schedulingPageCount = useMemo(() => {
    const schedulingPage = data.pages.find(
      (p) => p.id === "scheduling-1" && p.pageType === "scheduling"
    ) as SchedulingPage | undefined;
    return schedulingPage ? calculateSchedulingPageCount(schedulingPage) : 0;
  }, [data.pages]);

  return (
    <>
      {/* Cover Page */}
      {(() => {
        const coverPage = data.pages.find(
          (p) => p.id === "cover-page" && p.pageType === "cover"
        ) as CoverPage | undefined;
        return coverPage ? (
          <SectionCoverPage
            key={coverPage.id}
            sectionNumber={coverPage.sectionNumber}
            sectionTitle={coverPage.sectionTitle}
            sectionDescription={coverPage.sectionDescription}
            companyName={coverPage.companyName}
          />
        ) : null;
      })()}

      {/* Scheduling Page */}
      {(() => {
        const schedulingPage = data.pages.find(
          (p) => p.id === "scheduling-1" && p.pageType === "scheduling"
        ) as SchedulingPage | undefined;
        return schedulingPage ? (
          <ServiceSchedulingPage
            key="scheduling-1"
            page={schedulingPage}
            pageIndex={0}
          />
        ) : null;
      })()}

      {/* Service Pages */}
      {((): React.ReactNode => {
        const servicePage = data.pages.find(
          (p) => p.id === "services-page-1" && p.pageType === "services"
        ) as ServicesPage | undefined;
        if (!servicePage) return null;

        return servicePage.services.map((service, serviceIndex) => (
          <ServicePage
            key={`${servicePage.id}-${serviceIndex}`}
            companyName={servicePage.companyName}
            sectionName={servicePage.sectionName}
            page={service}
            pageIndex={serviceIndex + schedulingPageCount}
            labels={servicePage.labels}
            unitType={servicePage.unitType}
          />
        ));
      })()}
    </>
  );
};

export default NewReportPagesContent;
